import * as analytics from "analytics";
import * as sidebarSettings from "sidebarExpandedSetting";
import { schedulePopoverReposition } from "views/readerText/ReaderPopoverLayer";
import * as walkthrough from "walkthrough";

type Deps = {
  getShowSideBarCloseHint: () => boolean;
  getSideBarVisible: () => boolean;
  setSidebarHint: (
    sidebarHint: { text: string; title: string } | undefined
  ) => void;
  setShowSideBarCloseHint: (showSideBarCloseHint: boolean) => void;
  setSideBarAutoToggled: (sideBarAutoToggled: boolean) => void;
  setSideBarVisible: (sideBarVisible: boolean) => void;
  showContentTab: () => Promise<void>;
  showDictionaryIfVisible: () => void;
  showSmartDictionaryIfAppropriate: () => Promise<boolean | void> | void;
  updateLayout: (retainPosition?: number) => void;
};

const toggleTime = 200;
const sidebarThreshold = 600;

export const createReaderPageSidebarEffects = (deps: Deps) => {
  let clickToCancelSidebarHintTimeout:
    | ReturnType<typeof setTimeout>
    | undefined;
  let previousWindowWidth = window.innerWidth;
  // True while the explain nudge tooltip is the hint currently on screen, so we
  // can attribute an explicit close-button dismissal to the nudge (and not to
  // the unrelated "Explore the sidebar" hint shown after opening).
  let explainNudgeVisible = false;

  /**
   * @param dismissedByUser - true when the user actively closed the hint via the
   *   tooltip's "x" button. Opening the sidebar also clears the hint, but that is
   *   the success outcome, not a dismissal, so it must not be tracked as one.
   */
  const dismissSidebarHint = (options?: { dismissedByUser?: boolean }) => {
    if (options?.dismissedByUser && explainNudgeVisible) {
      // Nudge dismissal rate: same `Hint`/`openSidebar` event as the shown
      // nudge, flagged with `Dismissed` so we can monitor dismissed ÷ shown.
      analytics.trackEventWithProperties("Hint-openSidebar", {
        Dismissed: true,
      });
    }
    explainNudgeVisible = false;
    deps.setSidebarHint(undefined);
    walkthrough.setAchievement("readerSidebar", true);
  };

  /**
   * On a small-screen user's first single-word translation, nudge them once
   * to open the sidebar so they discover the Smart Dictionary AI explanation
   * (which auto-opens on large screens but not on phones). Shipped from the
   * explainNudge experiment, see docs/experiments/2026-05-explainNudge.md.
   */
  const maybeShowExplainNudge = (userWord: { state: { value: any } }) => {
    // Large screens already auto-open the sidebar, so there is nothing to nudge.
    if (sidebarSettings.persistentMode()) {
      return;
    }

    // Skip if the sidebar is already open or the user has already discovered it.
    if (
      deps.getSideBarVisible() ||
      walkthrough.hasAchievement("readerSidebar")
    ) {
      return;
    }

    // Only nudge on single-word translations (phrases contain a space).
    const word: string = userWord?.state?.value?.word ?? "";
    if (!word || / /.test(word)) {
      return;
    }

    deps.setSidebarHint({
      title: "Explanation",
      text: "Tap here to see an explanation of this word.",
    });
    explainNudgeVisible = true;
    analytics.trackEvent("Hint-openSidebar");

    // Show the nudge only once, even across sessions.
    walkthrough.setAchievement("readerSidebar", true);
  };

  /**
   * Hides the explain nudge once its word has left the screen (any reader
   * navigation, or finishing the text), so it doesn't linger over the next
   * page or the end-of-text page.
   */
  const hideExplainNudge = () => {
    if (explainNudgeVisible) {
      dismissSidebarHint();
    }
  };

  /** Resolves once the text has been re-laid out for the new column width */
  const toggleSidebar = (retainPosition?: number) => {
    // Preact owns the sidebar and container positions; this module only keeps
    // the legacy side effects that still surround the toggle behavior.
    const willShowSideBar = !deps.getSideBarVisible();

    deps.setSideBarAutoToggled(true);
    dismissSidebarHint();
    document
      .querySelector<HTMLElement>("#streak")
      ?.style.removeProperty("display");

    if (willShowSideBar) {
      if (window.innerWidth > 754 && deps.getShowSideBarCloseHint()) {
        deps.setShowSideBarCloseHint(false);
        clickToCancelSidebarHintTimeout = setTimeout(() => {
          deps.setSidebarHint({
            title: "Sidebar",
            text: "Explore the other options in the sidebar, and click ☰ again to close.",
          });
        }, 4000);
      }

      deps.setSideBarVisible(true);
      void deps.showContentTab();
      deps.showDictionaryIfVisible();
      deps.showSmartDictionaryIfAppropriate();
      document.body.classList.add("sideBarVisible");
    } else {
      if (clickToCancelSidebarHintTimeout) {
        clearTimeout(clickToCancelSidebarHintTimeout);
        clickToCancelSidebarHintTimeout = undefined;
      }
      deps.setSidebarHint(undefined);

      deps.setSideBarVisible(false);
      document.body.classList.remove("sideBarVisible");
    }

    if (sidebarSettings.persistentMode()) {
      sidebarSettings.setExpanded(willShowSideBar);
    }

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        deps.updateLayout(retainPosition);
        schedulePopoverReposition();
        resolve();
      }, toggleTime);
    });
  };

  const handleResize = () => {
    const currentWindowWidth = window.innerWidth;

    if (
      previousWindowWidth > sidebarThreshold &&
      currentWindowWidth <= sidebarThreshold &&
      deps.getSideBarVisible()
    ) {
      toggleSidebar();
    }

    previousWindowWidth = currentWindowWidth;
  };

  const cleanup = () => {
    if (clickToCancelSidebarHintTimeout) {
      clearTimeout(clickToCancelSidebarHintTimeout);
      clickToCancelSidebarHintTimeout = undefined;
    }
  };

  return {
    cleanup,
    dismissSidebarHint,
    handleResize,
    hideExplainNudge,
    maybeShowExplainNudge,
    toggleSidebar,
  };
};
